import React from "react"
import { useHistory } from "react-router-dom"
import { syncSaasSelectedTenantOnboardingFlags } from "../../utils/tenantOnboardingFlags"
import styled from "styled-components"
import { Button } from "@material-ui/core"
import SwapHorizIcon from "@material-ui/icons/ArrowBack"

const ACCENT = "var(--primaryColor)"

const Card = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 92px;
  padding: 16px 20px;
  margin-bottom: 7px;
  margin-top: 12px;
  background-color: #ffffff;
  background-size: 14px 14px;
  border: 1px solid #efeaf4;
  border-radius: 16px;
  box-shadow: 0 8px 24px rgba(40, 16, 48, 0.08);
  box-sizing: border-box;
  max-width: 480px;

  @media (max-width: 900px) {
    max-width: 100%;
  }

  @media (max-width: 600px) {
    flex-wrap: wrap;
    align-items: flex-start;
    min-height: unset;
    padding: 14px;
    margin-bottom: 16px;
    border-radius: 14px;
  }

  @media (max-width: 380px) {
    padding: 12px;
  }
`

const Avatar = styled.div`
  width: 56px;
  height: 56px;
  flex-shrink: 0;
  border-radius: 50%;
  background: #ffffff;
  box-shadow: 0 8px 22px rgba(176, 20, 128, 0.22);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 16px;

  svg {
    width: 28px;
    height: 28px;
  }

  @media (max-width: 600px) {
    width: 44px;
    height: 44px;
    margin-right: 12px;

    svg {
      width: 22px;
      height: 22px;
    }
  }
`

const Info = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  flex: 1;

  @media (max-width: 600px) {
    flex: 1 1 calc(100% - 56px);
  }
`

const Label = styled.div`
  font-size: 12px;
  line-height: 16px;
  font-weight: 500;
  color: #8a8494;
  margin-bottom: 2px;

  @media (max-width: 600px) {
    font-size: 11px;
    line-height: 14px;
  }
`

const Name = styled.div`
  font-size: 22px;
  line-height: 28px;
  font-weight: 700;
  color: #2a2233;
  letter-spacing: -0.02em;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  word-break: break-word;
  overflow-wrap: anywhere;

  @media (max-width: 600px) {
    font-size: 18px;
    line-height: 24px;
  }

  @media (max-width: 380px) {
    font-size: 16px;
    line-height: 22px;
  }
`

const Status = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 6px;
  width: fit-content;
  padding: 2px 10px 2px 8px;
  border-radius: 999px;
  background: ${(props) => (props.$active ? "#e8f7ee" : "#f4f1f6")};
  color: ${(props) => (props.$active ? "#1f8a3b" : "#6b6377")};
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;

  @media (max-width: 600px) {
    margin-top: 4px;
    font-size: 11px;
    line-height: 16px;
    padding: 2px 8px 2px 6px;
  }
`

const StatusDot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: currentColor;
`

const SwitchButton = styled(Button)`
  && {
    flex-shrink: 0;
    margin-left: 16px;
    min-width: 168px;
    height: 35px;
    padding: 0 14px;
    border-radius: 10px;
    background: ${ACCENT};
    color: #ffffff;
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: none;
    box-shadow: none;

    .MuiButton-startIcon {
      margin-right: 8px;
      margin-left: -2px;
    }

    svg {
      font-size: 20px;
    }

    &:hover {
      background: ${ACCENT};
      opacity: 0.94;
      box-shadow: none;
    }

    @media (max-width: 600px) {
      width: 100%;
      min-width: 0;
      flex-basis: 100%;
      margin-left: 0;
      margin-top: 12px !important;
      height: 40px;
      font-size: 13px;
    }
  }
`
const SwitchButtonLink = styled(Button)`
  && {
    width: fit-content;
    flex-shrink: 0;
    margin-left: 0px;
    height: 35px;
    padding: 0 0px;
    border-radius: 10px;
    background: transparent;
    color: ${ACCENT};
    font-size: 14px;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: none;
    box-shadow: none;
    margin-bottom: 20px !important;

    .MuiButton-startIcon {
      margin-right: 8px;
      margin-left: -2px;
    }

    svg {
      font-size: 20px;
    }

    &:hover {
      background: transparent;
      color: ${ACCENT};
      box-shadow: none;
    }

    @media (max-width: 600px) {
      width: 100%;
      min-width: 0;
      flex-basis: 100%;
      margin-left: 0;
      margin-top: 12px !important;
      height: 40px;
      font-size: 13px;
    }
  }
`

const BuildingIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path
      d="M4.5 21V6.8c0-.7.4-1.3 1.1-1.5l6.2-2.1c.8-.3 1.6.3 1.6 1.1V21"
      stroke={ACCENT}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M13.4 9.2h5c.6 0 1.1.5 1.1 1.1V21"
      stroke={ACCENT}
      strokeWidth="1.7"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M3.5 21h17"
      stroke={ACCENT}
      strokeWidth="1.7"
      strokeLinecap="round"
    />
    <path
      d="M7.2 9.2h2.1M7.2 12.4h2.1M7.2 15.6h2.1M15.6 12.6h1.8M15.6 15.6h1.8"
      stroke={ACCENT}
      strokeWidth="1.7"
      strokeLinecap="round"
    />
  </svg>
)

function SwitchTenant(props) {
  const {
    tenantName = "",
    tenantUrl = "",
    status = "Active",
    onSwitch = null,
    buttonName = "Switch Tenant",
  } = props || {}
  const history = useHistory()
  const isActive = String(status).toLowerCase() === "active"

  const handleSwitch = async () => {
    localStorage.setItem("selectedDefaultTenantId", "")
    localStorage.setItem("selectedDefaultRealmValue", "")
    localStorage.setItem("selectedTenantName", "")
    localStorage.setItem("selectedTenantStatus", "")
    localStorage.setItem("selectedTenantStoreTypes", "")
    await syncSaasSelectedTenantOnboardingFlags()

    if (typeof onSwitch === "function") {
      onSwitch()
      return
    }
    if (tenantUrl) {
      history.push(tenantUrl)
    }
  }

  return (
    <SwitchButtonLink
      variant="contained"
      disableElevation
      startIcon={<SwapHorizIcon />}
      onClick={handleSwitch}
    >
      {"Back to Tenant"}
    </SwitchButtonLink>
  )

  //   return (
  //     <Card>
  //       <Avatar>
  //         <BuildingIcon />
  //       </Avatar>
  //       <Info>
  //         <Label>Tenant Name</Label>
  //         <Name title={tenantName || "EMPTY"}>{tenantName || "EMPTY"}</Name>
  //         <Status $active={isActive}>
  //           <StatusDot />
  //           {isActive ? "Active" : status || "Inactive"}
  //         </Status>
  //       </Info>
  //       <SwitchButton
  //         variant="contained"
  //         disableElevation
  //         startIcon={<SwapHorizIcon />}
  //         onClick={handleSwitch}
  //       >
  //         {buttonName}
  //       </SwitchButton>
  //     </Card>
  //   )
}

export { SwitchTenant }
export default SwitchTenant
